import React, { createContext, useContext, useState, useCallback } from 'react';

const ApolloContext = createContext(null);

export function ApolloProvider({ children }) {
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [activeBrokerId, setActiveBrokerId] = useState(null);
  const [activeSessionId, setActiveSessionId] = useState(null);

  // Chat context — linked resources that influence broker decisions
  const [linkedContext, setLinkedContext] = useState({
    strategies: [],   // strategy IDs linked to chat
    brokers: [],      // broker connection IDs
    providers: [],    // market data provider IDs
    symbols: [],      // symbols for market data context
  });

  // Chat parameters
  const [chatParams, setChatParams] = useState({
    model: 'sonnet',       // sonnet or haiku
    temperature: 0.3,
    maxTokens: 4096,
  });

  // Workflow editor state — selected node for property editing in drawer
  const [selectedNode, setSelectedNode] = useState(null);
  const [workflowMode, setWorkflowMode] = useState(false); // true when on workflow editor page

  const addLinkedItem = useCallback((type, item) => {
    setLinkedContext((prev) => {
      if (prev[type]?.some((i) => i.id === item.id)) return prev;
      return { ...prev, [type]: [...(prev[type] || []), item] };
    });
  }, []);

  const removeLinkedItem = useCallback((type, itemId) => {
    setLinkedContext((prev) => ({
      ...prev,
      [type]: (prev[type] || []).filter((i) => i.id !== itemId),
    }));
  }, []);

  const clearLinkedContext = useCallback(() => {
    setLinkedContext({ strategies: [], brokers: [], providers: [], symbols: [] });
  }, []);

  return (
    <ApolloContext.Provider
      value={{
        drawerOpen,
        setDrawerOpen,
        activeBrokerId,
        setActiveBrokerId,
        activeSessionId,
        setActiveSessionId,
        linkedContext,
        setLinkedContext,
        addLinkedItem,
        removeLinkedItem,
        clearLinkedContext,
        chatParams,
        setChatParams,
        selectedNode,
        setSelectedNode,
        workflowMode,
        setWorkflowMode,
      }}
    >
      {children}
    </ApolloContext.Provider>
  );
}

export function useApollo() {
  const ctx = useContext(ApolloContext);
  if (!ctx) throw new Error('useApollo must be used within ApolloProvider');
  return ctx;
}
